import {
	Box,
	Unstable_Grid2 as Grid,
	Typography,
	DialogTitle,
	Dialog,
	CircularProgress,
	Divider,
	DialogActions,
	Button,
	DialogContent,
	IconButton
} from "@mui/material";
import { CalendarMonth, Delete } from "@mui/icons-material";
import { useRecord } from "../../../api/useRecord";
import { ClientSessionsSummary } from "../../clients/ClientSessionsSummary";
import { useClientSessionsSummary } from "../../../api/client-sessions-summary";
import { Link } from "react-router-dom";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { DoStuffAfterReturnType, GroupsOptions } from "../../../types/groups.types";
import { findField } from "../../../utils/findField";
import { SessionStatusField } from "./SessionStatusField";
import { formatGroups } from "../../../utils/formatGroups";
import { SessionDetailGroup } from "./SessionDetailGroup";
import { useFields } from "../../../api/useFields";
import { useDeleteRecord } from "../../../api/useDeleteRecord";
import { useCallback, useEffect } from "react";
import { useStore } from "../../../store/store";
import { Alerts, Notifications } from "../../../store/store.types";
import { useIsMobile } from "../../../utils/useIsMobile";
import { SessionTechnicianField } from "./SessionTechnicianField";
import { CalendarEvent } from "../../../components/calendar/calendar.types";
import { deleteGMeeting } from "../sessionsOptions";
import { SessionActionButtons } from "./SessionActionButtons";
import { CallButton } from "../../../components/phoneLetsCall/CallButton";

type EventPriviewProps = {
	previewIsOpen: boolean;
	event: CalendarEvent | undefined;
	setPreviewIsOpen: (value: boolean) => void;
	groupsOptions?: GroupsOptions;
	refetchSessions: () => void;
};

export const fieldsQuerySpecific = [
	"*",
	"user_created.id,user_created.first_name,user_created.last_name,user_created.avatar",
	"user_updated.id,user_updated.first_name,user_updated.last_name,user_updated.avatar",
	"technician.id,technician.color,technician.user_id.id,technician.user_id.first_name,technician.user_id.last_name,technician.user_id.avatar",
	"service.id,service.name,service.can_be_online",
	"clinic_id.id,clinic_id.name",
	"treatment.id,treatment.treatment_status_new,treatment.client.id,treatment.client.client_id,treatment.client.user_id.id,treatment.client.user_id.first_name,treatment.client.user_id.last_name,treatment.client.user_id.phone,treatment.client.user_id.email,treatment.client.user_id.avatar",
	"lead.id,lead.first_name,lead.last_name,lead.phone",
	"internal_meetings_participants.technicians_id.id,internal_meetings_participants.technicians_id.user_id.id,internal_meetings_participants.technicians_id.user_id.first_name,internal_meetings_participants.technicians_id.user_id.last_name,internal_meetings_participants.technicians_id.user_id.avatar"
].join(",");

const mapEventToInitialData = (event: CalendarEvent | undefined) => {
	if (!event) return undefined;
	return {
		item: {
			id: event.id,
			title: (event as any).originalTitle,
			status: event.status,
			start_date: (event as any).start_date,
			end_date: (event as any).end_date,
			notes: event.notes,
			is_all_day: event.allDay,
			meeting_id: (event as any).meeting_id,
			meeting_url: (event as any).meeting_url,
			service: (event as any).service,
			treatment: event.treatment,
			technician: {
				color: (event as any).technician?.color,
				id: (event as any).technician?.id,
				user_id: {
					avatar: (event as any).technician?.user_id?.avatar,
					id: (event as any).technician?.user_id?.id,
					first_name: "",
					last_name: ""
				}
			},
			clinic_id: (event as any).clinic_id,
			lead: (event as any).lead,
			internal_meetings_participants: (event as any).internal_meetings_participants
		},
		id: event.id,
		entity: "sessions"
	};
};

const SESSION_PREVIEW_STALE_TIME_MS = 15 * 1000;

export const SessionPreview = ({
	event,
	previewIsOpen,
	setPreviewIsOpen,
	groupsOptions,
	refetchSessions
}: EventPriviewProps) => {
	const eventId = event?.id;
	const initialData = mapEventToInitialData(event);
	const { data, status, fetchStatus, isStale, refetch } = useRecord("sessions", fieldsQuerySpecific, eventId, {
		enabled: true,
		initialData,
		staleTime: SESSION_PREVIEW_STALE_TIME_MS
	});

	useEffect(() => {
		if (previewIsOpen && isStale) refetch({ cancelRefetch: false });
	}, [previewIsOpen, eventId, isStale, refetch]);

	const treatmentId = data?.item?.treatment?.id;
	const clientId = data?.item?.treatment?.client?.id;
	const { alerts, notifications } = useStore();
	const { formatedFields, status: statusFields } = useFields("sessions");

	const { data: sessions, status: sessionsStatus } = useClientSessionsSummary(clientId, treatmentId);
	const updateSession = useUpdateRecord();

	const mutation = useDeleteRecord();
	const isMobile = useIsMobile();

	const doStuffAfterDelete = useCallback(
		async (
			setPreviewIsOpen: (value: boolean) => void,
			refetchSessions: () => void,
			alerts: Alerts,
			notifications: Notifications,
			deleteGMeeting: (meetingId: string) => Promise<DoStuffAfterReturnType | undefined>
		) => {
			alerts.setIsOpen(false);
			setPreviewIsOpen(false);
			if (data?.item?.meeting_id) {
				const response = await deleteGMeeting(data.item.meeting_id);
				notifications.setNotification({
					isOpen: true,
					message: response?.message,
					severity: response?.severity
				});
			}
			refetchSessions();
			return;
		},
		[data]
	);

	useEffect(() => {
		if (alerts.allowExecution && data) {
			mutation.mutate({
				entity: "sessions",
				id: data.item?.id,
				doStuffAfterDelete: () =>
					doStuffAfterDelete(setPreviewIsOpen, refetchSessions, alerts, notifications, deleteGMeeting)
			});
			alerts.setAllowExecution(false);
		}
	}, [
		alerts,
		alerts.allowExecution,
		data,
		mutation,
		refetchSessions,
		setPreviewIsOpen,
		doStuffAfterDelete,
		notifications
	]);

	if (fetchStatus === "fetching" && !data?.item)
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					zIndex: 9999 // ensure it's on top of everything else
				}}
			>
				<CircularProgress size={100} />
			</Box>
		);

	if (status !== "success" || statusFields !== "success") return null;

	const { item } = data;
	const { groups } = formatGroups(formatedFields, item, "detail");

	const statusField = findField(formatedFields, "status");
	const phone = item?.treatment?.client?.user_id?.phone ?? item?.lead?.phone;

	return (
		<Dialog
			open={previewIsOpen}
			onClose={() => {
				setPreviewIsOpen(false);
			}}
			maxWidth="lg"
			fullWidth
		>
			<DialogContent sx={{ padding: 5 }}>
				<DialogTitle sx={{ display: "flex", alignItems: "center", pb: 2 }}>
					<Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
						<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
							<CalendarMonth />
							<Typography variant="h6">Detalhe da Sessão</Typography>
							{phone && <CallButton phone={phone} />}
						</Box>
						<SessionActionButtons session={item} />
					</Box>
				</DialogTitle>
				<Divider />
				<Grid
					container
					spacing={1}
					rowSpacing={4}
					justifyContent="flex-start"
					alignItems="top"
					sx={{ padding: 4, minHeight: "500px" }}
					flexDirection="row"
					lg={12}
				>
					<Grid xs={12} lg={4}>
						<SessionDetailGroup group={groups[0]} item={item} groupOptions={groupsOptions} />
					</Grid>
					<Grid xs={12} lg={3.9}>
						<SessionDetailGroup group={groups[1]} item={item} groupOptions={groupsOptions} />
					</Grid>
					<Grid xs={12} lg={0.1}>
						<Divider orientation="vertical" />
					</Grid>
					<Grid sx={{ pl: { xs: 0, lg: 4 } }} xs={12} ml={isMobile ? 0 : 10} lg={3.5}>
						<ClientSessionsSummary
							sessions={sessions}
							treatmentId={treatmentId}
							options={{ flexDirection: "column", presentation: "sessionPreview" }}
							isLoading={sessionsStatus === "loading"}
						/>
					</Grid>
				</Grid>

				<DialogActions sx={{ p: 2, mt: 4 }}>
					<Grid
						container
						spacing={1}
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						flexGrow={1}
					>
						<Grid
							xs={12}
							lg={6}
							display="flex"
							flexDirection="row"
							gap={4}
							alignItems="flex-start"
							justifyContent="space-between"
						>
							{updateSession.isLoading ? (
								<CircularProgress />
							) : (
								statusField &&
								eventId && (
									<SessionStatusField
										field={statusField}
										value={item?.status}
										event={event}
										session={item}
										setPreviewIsOpen={setPreviewIsOpen}
										refetchSessions={refetchSessions}
									/>
								)
							)}
							{eventId && (
								<SessionTechnicianField
									value={item?.technician?.id}
									eventId={eventId}
									setPreviewIsOpen={setPreviewIsOpen}
									refetchSessions={refetchSessions}
								/>
							)}
						</Grid>

						<Grid>
							<Button size="large" component={Link} to={`/sessions/${item?.id}/update`} sx={{ ml: 1 }}>
								Editar
							</Button>
							<IconButton
								onClick={() => {
									alerts.setIsOpen(true);
									alerts.setMessage(createAlertMessage(item));
								}}
								size="large"
								color="error"
							>
								<Delete />
							</IconButton>
						</Grid>
					</Grid>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};

const createAlertMessage = (item: any) => {
	const client = item?.treatment?.client;
	const lead = item?.lead;
	const title = item?.title;

	if (client) {
		return `Esta ação é irreversivel! irá apagar a sessão associada a ${client?.client_id}`;
	}

	if (lead) {
		return `Esta ação é irreversivel! irá apagar a sessão associada a ${lead?.first_name} ${lead?.last_name}`;
	}

	if (title) {
		return `Esta ação é irreversivel! irá apagar a sessão associada a ${title}`;
	}

	return `Esta ação é irreversivel! irá apagar a sessão`;
};
